import React, { useState, useEffect } from 'react';
import { CalendarDaysIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';

interface MeetingNote {
  id: string;
  title: string;
  date: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  lastModified: string;
}

const MeetingNotes: React.FC = () => {
  const [meetingNotes, setMeetingNotes] = useState<MeetingNote[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMeetingNotes();
  }, []);

  const loadMeetingNotes = async () => {
    setLoading(true);
    try {
      const data = await apiService.getMeetingNotes();
      const notes = Array.isArray(data) ? data.filter(item => item !== null) as unknown as MeetingNote[] : [];
      setMeetingNotes(notes);
    } catch (error) {
      console.error('Failed to load meeting notes:', error);
      setMeetingNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    return extension === 'pdf' ? '📄' : '📝';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-church-light to-white">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-church-dark mb-4">
              Parish Meeting Notes
            </h1>
            <div className="w-24 h-1 bg-church-gold mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">
              Stay informed with minutes and notes from our parish meetings
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-church-blue mx-auto mb-4"></div>
                <p className="text-gray-600">Loading meeting notes...</p>
              </div>
            ) : meetingNotes.length === 0 ? (
              <div className="text-center py-12">
                <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Meeting Notes Available</h3>
                <p className="text-gray-600">
                  Check back soon for minutes from our latest parish meetings.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold text-church-dark mb-4">
                    Available Meeting Notes
                  </h2>
                  <p className="text-gray-600">
                    Click on any document below to view or download the meeting notes.
                  </p>
                </div>

                <div className="grid gap-6">
                  {meetingNotes.map((note) => (
                    <div key={note.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-3">
                            <DocumentTextIcon className="h-6 w-6 text-church-blue mr-3" />
                            <h3 className="text-xl font-semibold text-church-dark">
                              {note.title}
                            </h3>
                          </div>
                          
                          <div className="flex items-center text-gray-600 mb-4 space-x-6">
                            <div className="flex items-center">
                              <CalendarDaysIcon className="h-4 w-4 mr-2" />
                              <span className="text-sm">
                                {formatDate(note.date)}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <DocumentTextIcon className="h-4 w-4 mr-2" />
                              <span className="text-sm">
                                {formatFileSize(note.fileSize)}
                              </span>
                            </div>
                          </div>

                          <p className="text-gray-700 text-sm mb-4 flex items-center">
                            <span className="mr-2">{getFileIcon(note.fileName)}</span>
                            {note.fileName}
                          </p>
                        </div>

                        <div className="ml-6 flex flex-col space-y-2">
                          <a
                            href={note.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-church-blue hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 text-center"
                          >
                            View Document
                          </a>
                          <a
                            href={note.fileUrl}
                            download={note.fileName}
                            className="bg-church-gold hover:bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 text-center"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-church-light rounded-lg">
                  <h3 className="text-lg font-semibold text-church-dark mb-3">
                    About Parish Meetings
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Our parish meetings are held to discuss important matters affecting 
                    our community. Meeting notes are published here to keep all 
                    parishioners informed about decisions, upcoming projects, and parish business.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    If you have questions about any meeting or would like to attend future meetings, 
                    please contact the parish office at{' '}
                    <a href="mailto:allsaintsthirsk@rcdmidd.org.uk" className="text-church-blue hover:underline">
                      allsaintsthirsk@rcdmidd.org.uk
                    </a>{' '}
                    or call us at{' '}
                    <a href="tel:01845527011" className="text-church-blue hover:underline">
                      01845 527011
                    </a>.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingNotes;